import { useEffect, useState } from "react";
import { Mui, MuiX, DateFns } from "@osu/react-ui";

export function DateRangeFilter({ column, getColumnFilterValue, setColumnFilterValue, filterModel }) {
  const columnName = column.columnDef?.header || column.header || '';
  const [localRange, setLocalRange] = useState(
    getColumnFilterValue(column.id) || [null, null]
  );

  const handleStartDateChange = (newStart) => {
    setLocalRange(prev => [newStart, prev[1]]);
  };

  const handleEndDateChange = (newEnd) => {
    setLocalRange(prev => [prev[0], newEnd]);
  };

  const handleApply = () => {
    setColumnFilterValue(column.id, localRange, "date-range");
  };
  
  // Keep track of external changes (e.g.: clear filters)
  useEffect(() => {
    setTimeout(() => {
        const newValue = filterModel.find(filter => filter.id === column.id)?.value;
        if (newValue?.[0] === localRange?.[0] && newValue?.[1] === localRange?.[1]) { return; }
        setLocalRange(() => newValue ?? [null, null]);
    }, 250);
  }, [JSON.stringify(filterModel)]);

  return (
    <Mui.Box>
      <DateFns.LocalizationProvider>
        <MuiX.DatePicker
          label={`${columnName} start date`}
          value={localRange[0]}
          onChange={handleStartDateChange}
          sx={{ minWidth: 140, mb: 1 }}
        />
        <MuiX.DatePicker
          label={`${columnName} end date`}
          value={localRange[1]}
          onChange={handleEndDateChange}
          sx={{ minWidth: 140 }}
        />
      </DateFns.LocalizationProvider>
      <Mui.Button
        variant="contained"
        size="small"
        sx={{ mt: 1 }}
        aria-label={`Apply ${columnName} date filter`}
        onClick={handleApply}
      >
        Apply
      </Mui.Button>
    </Mui.Box>
  );
}

export function RangeFilter({ column, getColumnFilterValue, setColumnFilterValue, filterModel }) {
  const columnName = column.columnDef?.header || column.header || '';
  const [localRange, setLocalRange] = useState(
    getColumnFilterValue(column.id) || [null, null]
  );

  const handleMinChange = (e) => {
    const value = e.target.value === "" ? null : Number(e.target.value);
    setLocalRange(prev => [value, prev[1]]);
  };

  const handleMaxChange = (e) => {
    const value = e.target.value === "" ? null : Number(e.target.value);
    setLocalRange(prev => [prev[0], value]);
  };

  const handleApply = () => {
    setColumnFilterValue(column.id, localRange, "range");
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleApply();
    }
    if (e.key === ' ') {
      e.stopPropagation();
    }
  };
  
  // Keep track of external changes (e.g.: clear filters)
  useEffect(() => {
    setTimeout(() => {
        const newValue = filterModel.find(filter => filter.id === column.id)?.value;
        if (newValue?.[0] === localRange?.[0] && newValue?.[1] === localRange?.[1]) { return; }
        setLocalRange(() => newValue ?? [null, null]);
    }, 250);
  }, [JSON.stringify(filterModel)]);

  return (
    <Mui.Box>
      <Mui.TextField
        label={`${columnName} min`}
        type="number"
        value={localRange[0] ?? ""}
        onChange={handleMinChange}
        onKeyDown={handleKeyDown}
        sx={{ minWidth: 140, mb: 1 }}
      />
      <Mui.TextField
        label={`${columnName} max`}
        type="number"
        value={localRange[1] ?? ""}
        onChange={handleMaxChange}
        onKeyDown={handleKeyDown}
        sx={{ minWidth: 140 }}
      />
      <Mui.Button
        variant="contained"
        size="small"
        sx={{ mt: 1 }}
        aria-label={`Apply ${columnName} range filter`}
        onClick={handleApply}
      >
        Apply
      </Mui.Button>
    </Mui.Box>
  );
}

export function TextFilters({ column, getColumnFilterValue, setColumnFilterValue, filterType, filterModel }) {
  const columnName = column.columnDef?.header || column.header || '';
  const initialValue = getColumnFilterValue(column.id);
  const [localValue, setLocalValue] = useState(
    Array.isArray(initialValue) ? "" : (initialValue ?? "")
  );

  const handleChange = (e) => {
    setLocalValue(e.target.value);
  };

  const handleApply = () => {
    setColumnFilterValue(column.id, localValue, filterType);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleApply();
    }
    if (e.key === ' ') {
      e.stopPropagation();
    }
  };
  
  // Keep track of external changes (e.g.: clear filters)
  useEffect(() => {
    setTimeout(() => {
        const newValue = filterModel.find(filter => filter.id === column.id)?.value;
        if (newValue === localValue) { return; }
        setLocalValue(() => newValue ?? "");
    }, 250);
  }, [JSON.stringify(filterModel)]);

  return (
    <Mui.Box>
      <Mui.TextField
        label={`Filter ${columnName}`}
        value={localValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        size="small"
        sx={{ minWidth: 140, mb: 1 }}
      />
      <Mui.Button
        variant="contained"
        size="small"
        sx={{ mt: 1 }}
        aria-label={`Apply ${columnName} filter`}
        onClick={handleApply}
      >
        Apply
      </Mui.Button>
    </Mui.Box>
  );
}